import React, {createContext, useCallback, useContext, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import Log from "../common/Log";
import {cache_actions} from "../redux/slice/CacheReducer";
import {account_actions} from "../redux/slice/AccountReducer";
import CourseModel from "../model/CourseModel";
import CommonModel from "../model/CommonModel";
import AccountModel from "../model/AccountModel";

// @TODO lru cache 방안
const default_cache_policy = {
    expire_time: 1000 * 60 * 60 * 24, // in millis (24 hours)
    // expire_time: 1000 * 5, // in millis (24 hours)
};

export const RESPONSE_INTERRUPTED = "response-interrupted";

const model_context = createContext();
export const ModelContextProvider = ({enable_cache = false, children}) => {
    const account_reducer = useSelector((state) => state.account_reducer);
    const cache_reducer = useSelector((state) => state.cache_reducer);
    const dispatch = useDispatch();

    const model_instances = useRef({
        common_model: new CommonModel(),
        account_model: new AccountModel(),
        course_model: new CourseModel(),
    });

    const _getKey = useCallback((request_obj) => {
        return typeof request_obj === "string" ? request_obj : JSON.stringify(request_obj);
    }, []);

    const _remove = useCallback(
        (request_obj) => {
            dispatch(cache_actions.remove(_getKey(request_obj)));
        },
        [dispatch, _getKey]
    );

    const get = useCallback(
        (request_obj) => {
            let saved = cache_reducer[_getKey(request_obj)]; // {value, cache_policy, time} @see CacheStore.actions.set 참고
            if (saved?.value) {
                let is_cache_clear = false;
                let cache_policy = saved.cache_policy;
                if (cache_policy) {
                    // @TODO redux 에 function(cache validate) 넣을수 있을까?
                    is_cache_clear =
                        saved.time <
                        (new Date().getTime() - cache_policy.expire_time || default_cache_policy.expire_time);
                } else {
                    // default cache rules
                    is_cache_clear = saved.time < new Date().getTime() - default_cache_policy.expire_time;
                }

                if (is_cache_clear) {
                    _remove(request_obj); // @TODO remove cache another old caches
                    saved = null;

                    Log.private("cache > removed old cache");
                } else {
                    Log.private("cache > hit");
                }
            } else {
                Log.private("cache > empty");
            }

            return saved?.value || null;
        },
        [cache_reducer, _getKey, _remove]
    );

    // cache_policy: { expire_time: {in_mlills}, cache_name: {"cache_name"}, cache_category: {"cache_category"}, ... }
    const set = useCallback(
        (request_obj, response_obj, cache_policy = null) => {
            dispatch(cache_actions.set({key: _getKey(request_obj), value: response_obj, cache_policy: cache_policy}));
            Log.private("cache > set");
        },
        [dispatch, _getKey]
    );

    // filter({value, cache_policy, time})=>bool
    const remove = useCallback(
        (filter) => {
            if (filter) {
                let cache_reducer_entries = Object.entries(cache_reducer);
                for (let [key, cache] of cache_reducer_entries) {
                    if (filter(cache)) {
                        _remove(key);
                        break;
                    }
                }
            }
        },
        [cache_reducer, _remove]
    );

    // filter({value, cache_policy, time})=>bool
    const removeAll = useCallback(
        (filter) => {
            if (filter) {
                let cache_reducer_entries = Object.entries(cache_reducer);
                for (let [key, cache] of cache_reducer_entries) {
                    if (filter(cache)) {
                        _remove(key);
                    }
                }
            } else {
                dispatch(cache_actions.removeAll());
            }
        },
        [cache_reducer, _remove, dispatch]
    );

    const isSuccess = useCallback((response) => {
        return response?.message === "success";
    }, []);

    const isError = useCallback((response) => {
        if (response === RESPONSE_INTERRUPTED) {
            return false;
        } else {
            return response?.message !== "success";
        }
    }, []);

    const cacheCheck = useCallback(
        (response) => {
            return isSuccess(response);
        },
        [isSuccess]
    );

    const memo_cache = useMemo(() => {
        return {
            get,
            set,
            remove,
            removeAll,
            check: cacheCheck,
        };
    }, [get, set, remove, removeAll, cacheCheck]);

    // @see model_instances
    const models = useMemo(() => {
        let actions = {
            onRequireLogin: async () => {
                dispatch(account_actions.onRequireLogin());
            },
            getToken: () => {
                return account_reducer.token || null;
            },
            cache: memo_cache,
            enable_cache,
        };
        Object.values(model_instances.current).forEach((model) => {
            model.setActions(actions);
        });
        return model_instances.current;
    }, [account_reducer.token, memo_cache, enable_cache, dispatch]);

    const memo_value = useMemo(() => {
        return {
            ...models,
            isSuccess,
            isError,
            cache: memo_cache,
        };
    }, [models, isSuccess, isError, memo_cache]);
    return <model_context.Provider value={memo_value}>{children}</model_context.Provider>;
};

export const useModelContext = () => {
    return useContext(model_context);
};
